import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Tag,
  Text,
  Td,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { getIncoming, updateIncoming, listIncomingSubmissions } from "../../../api/partnersApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  PARTNERS_INCOMING_STATUS_LABELS,
  PARTNERS_INCOMING_STATUS_COLORS,
  PARTNERS_SUBMISSION_STATUS_LABELS,
  PARTNERS_SUBMISSION_STATUS_COLORS,
} from "../../../constants/statuses";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";
import {
  DataTable,
  StatusBadge,
  EmptyState,
  TablePagination,
} from "../../../components/Portal/ui";
import SubmitCandidateModal from "./SubmitCandidateModal";
import SubmissionDetailModal from "./SubmissionDetailModal";
import { formatCost, formatAvailability, parseSkillList } from "./submissionFormatters";

// Label style for key/value detail fields.
const FIELD_LABEL_SX = {
  fontSize: "11px",
  fontWeight: 800,
  color: "portal.muted",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: "0 !important",
};

const PartnersIncomingDetail = ({ isOpen, onClose, incomingId, onStatusChange }) => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId } = useBusinessUnit();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Submissions
  const [submissions, setSubmissions] = useState([]);
  const [subPage, setSubPage] = useState(1);
  const [subPageSize, setSubPageSize] = useState(10);
  const [subTotal, setSubTotal] = useState(0);
  const [subTotalPages, setSubTotalPages] = useState(1);
  const [subLoading, setSubLoading] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);

  const submitModal = useDisclosure();

  const fetchDetail = useCallback(async () => {
    if (!incomingId) return;
    setLoading(true);
    try {
      const res = await getIncoming(incomingId, { buId: selectedBusinessUnitId || undefined });
      setDetail(res);
    } catch (e) {
      toast({ title: t("partners.incoming.fetchError", "Failed to load incoming mission"), status: "error", duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [incomingId, selectedBusinessUnitId, toast, t]);

  const fetchSubmissions = useCallback(async () => {
    if (!incomingId) return;
    setSubLoading(true);
    try {
      const res = await listIncomingSubmissions(incomingId, { page: subPage, pageSize: subPageSize }, { buId: selectedBusinessUnitId || undefined });
      setSubmissions(res.items || []);
      setSubTotal(res.totalItems || 0);
      setSubTotalPages(res.totalPages || 1);
    } catch (e) {
      // silent
    } finally {
      setSubLoading(false);
    }
  }, [incomingId, subPage, subPageSize, selectedBusinessUnitId]);

  useEffect(() => {
    if (isOpen && incomingId) {
      setDetail(null);
      setSubmissions([]);
      setSubPage(1);
      fetchDetail();
      fetchSubmissions();
    }
  }, [isOpen, incomingId, fetchDetail, fetchSubmissions]);

  const handleStatusUpdate = async (newStatus) => {
    setActionLoading(true);
    try {
      await updateIncoming(incomingId, { status: newStatus }, { buId: selectedBusinessUnitId || undefined });
      toast({
        title: newStatus === "accepted"
          ? t("partners.incoming.accepted", "Mission accepted")
          : t("partners.incoming.declined", "Mission declined"),
        status: "success",
        duration: 3000,
      });
      fetchDetail();
      onStatusChange?.();
    } catch (e) {
      const detail_msg = e?.response?.data?.detail || "Error";
      toast({ title: detail_msg, status: "error", duration: 4000 });
    } finally {
      setActionLoading(false);
    }
  };

  // Humanize missionPurpose exactly as the parent PartnersIncoming page does.
  const purposeLabel = (purpose) => {
    const map = {
      contact_pool: t("partners.missions.purposeLabels.contactPool", "Contact Pool"),
      propose_assignment: t("partners.missions.purposeLabels.proposeAssignment", "Assignment"),
    };
    return map[purpose] || purpose || "-";
  };

  const renderInfo = () => {
    if (!detail) return null;
    const skills = parseSkillList(detail.missionSkills);
    return (
      <Box>
        {/* ─── Top status/sender grid ─── */}
        <SimpleGrid columns={2} spacing={4} mb={4}>
          <Box>
            <Text sx={FIELD_LABEL_SX}>{t("partners.incoming.sender", "From")}</Text>
            <Text fontWeight={600} color="portal.text" fontSize="sm" sx={{ marginBottom: "0 !important" }}>
              {detail.senderCompanyName || `Company #${detail.senderCompanyId}`}
              {detail.senderBuName ? ` – ${detail.senderBuName}` : ""}
            </Text>
          </Box>
          <Box>
            <Text sx={FIELD_LABEL_SX}>{t("common.status", "Status")}</Text>
            <StatusBadge
              label={PARTNERS_INCOMING_STATUS_LABELS[detail.status] || detail.status}
              colorScheme={PARTNERS_INCOMING_STATUS_COLORS[detail.status] || "gray"}
            />
          </Box>
          <Box>
            <Text sx={FIELD_LABEL_SX}>{t("partners.missions.purpose", "Purpose")}</Text>
            <Text fontSize="sm" color="portal.text" sx={{ marginBottom: "0 !important" }}>
              {purposeLabel(detail.missionPurpose)}
            </Text>
          </Box>
        </SimpleGrid>

        {/* ─── Description ─── */}
        {detail.missionDescription && (
          <Box mb={4}>
            <Text sx={FIELD_LABEL_SX} mb={1}>{t("common.description", "Description")}</Text>
            <Text fontSize="sm" color="portal.text" whiteSpace="pre-wrap" sx={{ marginBottom: "0 !important" }}>
              {detail.missionDescription}
            </Text>
          </Box>
        )}

        {/* ─── Location / TJM / Dates ─── */}
        <SimpleGrid columns={2} spacing={4} mb={4}>
          {detail.missionLocation && (
            <Box>
              <Text sx={FIELD_LABEL_SX}>{t("partners.missions.location", "Location")}</Text>
              <Text fontSize="sm" color="portal.text" sx={{ marginBottom: "0 !important" }}>{detail.missionLocation}</Text>
            </Box>
          )}
          {(detail.missionTjmMin || detail.missionTjmMax) && (
            <Box>
              <Text sx={FIELD_LABEL_SX}>{t("partners.missions.tjm", "Daily Rate")}</Text>
              <Text fontSize="sm" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                {detail.missionTjmMin || "?"} - {detail.missionTjmMax || "?"} €
              </Text>
            </Box>
          )}
          {detail.missionStartDate && (
            <Box>
              <Text sx={FIELD_LABEL_SX}>{t("partners.missions.startDate", "Start Date")}</Text>
              <Text fontSize="sm" color="portal.text" sx={{ marginBottom: "0 !important" }}>{detail.missionStartDate}</Text>
            </Box>
          )}
          {detail.missionDurationDays && (
            <Box>
              <Text sx={FIELD_LABEL_SX}>{t("partners.missions.duration", "Duration")}</Text>
              <Text fontSize="sm" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                {detail.missionDurationDays} {t("common.days", "days")}
              </Text>
            </Box>
          )}
        </SimpleGrid>

        {/* ─── Skills ─── */}
        {skills.length > 0 && (
          <Box mb={4}>
            <Text sx={FIELD_LABEL_SX} mb={2}>{t("partners.missions.skills", "Skills")}</Text>
            <Flex flexWrap="wrap" gap={2}>
              {skills.map((skill, i) => (
                <Tag key={i} size="sm" variant="subtle" colorScheme="blue" fontWeight={600}>
                  {skill}
                </Tag>
              ))}
            </Flex>
          </Box>
        )}
      </Box>
    );
  };

  const submissionColumns = [
    { key: "candidate", label: t("partners.incoming.candidate", "Candidate"), width: "28%" },
    { key: "score", label: t("partners.incoming.score", "Score"), width: "10%" },
    { key: "cost", label: t("partners.missions.detail.cost", "Cost"), width: "16%" },
    { key: "availability", label: t("partners.missions.detail.availability", "Availability"), width: "16%" },
    { key: "status", label: t("common.status", "Status"), width: "16%" },
    { key: "date", label: t("common.createdAt", "Date"), width: "14%" },
  ];

  const renderSubmissions = () => (
    <Box>
      <Flex justify="space-between" align="center" mb={3}>
        <Heading
          size="sm"
          color="portal.text"
          fontWeight={800}
          sx={{ marginBottom: "0 !important" }}
        >
          {t("partners.incoming.submissions", "Submissions")} ({subTotal})
        </Heading>
        {detail && (detail.status === "pending" || detail.status === "accepted") && (
          <Button
            leftIcon={<AddIcon />}
            size="sm"
            variant="primary"
            onClick={submitModal.onOpen}
          >
            {t("partners.incoming.submitCandidate", "Submit Candidate")}
          </Button>
        )}
      </Flex>

      <DataTable
        columns={submissionColumns}
        rows={submissions}
        loading={subLoading}
        emptyState={
          <EmptyState
            title={t("partners.incoming.noSubmissions", "No candidates submitted yet.")}
          />
        }
        renderRow={(s) => (
          <Tr
            key={s.id}
            cursor="pointer"
            onClick={() => setSelectedSub(s)}
          >
            <Td>
              <Flex align="center" gap={1} flexWrap="wrap">
                <Text fontWeight={600} sx={{ marginBottom: "0 !important" }}>
                  {s.title || t("partners.missions.detail.submissionFallback", "Submission #{{id}}", { id: s.id })}
                </Text>
                {s.location && (
                  <Text fontSize="xs" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                    · {s.location}
                  </Text>
                )}
                {(s.sourceCvUrl || s.cvUrl) && (
                  <Badge colorScheme="blue" variant="subtle" fontSize="2xs">CV</Badge>
                )}
              </Flex>
            </Td>
            <Td>
              {s.score !== null && s.score !== undefined ? (
                <Badge colorScheme={s.score >= 80 ? "green" : s.score >= 50 ? "yellow" : "red"}>
                  {s.score}%
                </Badge>
              ) : "-"}
            </Td>
            <Td>{formatCost(s.costAmount, s.costType)}</Td>
            <Td>{formatAvailability(s.availabilityDate, s.availabilityStatus)}</Td>
            <Td>
              <StatusBadge
                label={PARTNERS_SUBMISSION_STATUS_LABELS[s.status] || s.status}
                colorScheme={PARTNERS_SUBMISSION_STATUS_COLORS[s.status] || "gray"}
              />
            </Td>
            <Td>{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : "-"}</Td>
          </Tr>
        )}
      />

      {submissions.length > 0 && (
        <TablePagination
          page={subPage}
          pageSize={subPageSize}
          totalItems={subTotal}
          totalPages={subTotalPages}
          onPageChange={setSubPage}
          onPageSizeChange={(sz) => { setSubPageSize(sz); setSubPage(1); }}
        />
      )}
    </Box>
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX} maxW="800px">
          <ModalHeader sx={MODAL_HEADER_SX}>
            {detail?.missionTitle || t("partners.incoming.detail", "Incoming Mission Detail")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {loading ? (
              <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
            ) : (
              <>
                {renderInfo()}
                <Divider my={5} />
                {renderSubmissions()}
              </>
            )}
          </ModalBody>

          {/* Accept / Decline actions — only shown while pending */}
          {detail?.status === "pending" && (
            <ModalFooter sx={MODAL_FOOTER_SX}>
              <Button
                variant="secondary"
                size="sm"
                onClick={onClose}
                isDisabled={actionLoading}
              >
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                colorScheme="red"
                variant="outline"
                size="sm"
                isLoading={actionLoading}
                onClick={() => handleStatusUpdate("declined")}
              >
                {t("partners.incoming.decline", "Decline")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                isLoading={actionLoading}
                onClick={() => handleStatusUpdate("accepted")}
              >
                {t("partners.incoming.accept", "Accept")}
              </Button>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>

      <SubmitCandidateModal
        isOpen={submitModal.isOpen}
        onClose={submitModal.onClose}
        incomingId={incomingId}
        onSuccess={() => { fetchSubmissions(); }}
      />

      <SubmissionDetailModal
        submission={selectedSub}
        onClose={() => setSelectedSub(null)}
      />
    </>
  );
};

export default PartnersIncomingDetail;
